import React, {useEffect, useReducer} from 'react';
import {Box} from '@mui/material';
import {useIntl} from 'react-intl';
import Selector from '../../../../../../components/shared/nextgen/selector';
import {FilterActionType, FilterChangeAction, FilterState} from '../../../../../../components/Schedule/header/components/filter/types';
import {REmployees} from '../../../../../../features/roles/types';
import {PIdNameModel} from '../../../../../../features/projects/types';
import {TiminatorType} from '../../../../../../features/types/types';
import TSwitch from '../../../../../../components/shared/form-controls/switch';
import {Lx} from '../../../../../../i18n/consts';
import { ActionCancelButtonsArea } from '../../../../../../components/shared/action-cancel-buttons-area';
import styles from './mobile-filter-sheet.module.scss';
import SearchInput from '../../../../../../components/shared/search-input';
import {DropdownWrapper} from '../../../../../../components/shared/nextgen/dropdown-wrapper';
import {VisibleDays} from '../../../../../../features/report/types';
import formDropdownStyles from '../../../../../../components/shared/form-controls/form-dropdown/form-dropdown.module.scss';

interface Props {
    open: boolean;
    onClose: () => void;
    onApply: (filter: FilterState) => Promise<void> | void;
    onClear: () => void;
    search: string;
    onSearchChange: (value: string) => void;
    filters: FilterState;
    employees: REmployees[];
    projects: PIdNameModel[];
    projectTypes: TiminatorType[];
    disabled?: boolean;
    detailedByDay: boolean;
    onDetailedByDayChange: (value: boolean) => void;
    visibleDays: VisibleDays;
    onVisibleDaysChange: (value: VisibleDays) => void;
    disableEmployeesFilter?: boolean;
}

interface ReducerState {
    draft: FilterState;
    current: FilterState;
}

const createEmptyFilterState = (): FilterState => ({
    selectedEmployees: [],
    selectedProjects: [],
    selectedProjectTypes: [],
    selectedShiftTypes: [],
    selectedAbsenceTypes: [],
    selectedStatuses: [],
    showAbsences: true,
});

const applyChange = (type: FilterActionType, payload: any, state: FilterState): FilterState => {
    const next = {...state};
    switch (type) {
        case 'EMPLOYEES_UPDATE':
            next.selectedEmployees = payload ?? [];
            break;
        case 'PROJECTS_UPDATE':
            next.selectedProjects = payload ?? [];
            break;
        case 'PROJECTTYPE_UPDATE':
            next.selectedProjectTypes = payload ?? [];
            break;
        case 'SHIFTTYPE_UPDATE':
            next.selectedShiftTypes = payload ?? [];
            break;
        case 'ABSENCETYPE_UPDATE':
            next.selectedAbsenceTypes = payload ?? [];
            break;
        case 'STATUSES_UPDATE':
            next.selectedStatuses = payload ?? [];
            break;
        case 'SHOW_ABSENCES_UPDATE':
            next.showAbsences = typeof payload === 'boolean' ? payload : true;
            break;
        default:
            break;
    }
    return next;
}

const initialReducerState: ReducerState = {
    current: createEmptyFilterState(),
    draft: createEmptyFilterState(),
};

function reducer(state: ReducerState, action: Partial<FilterChangeAction>): ReducerState {
    const nextState = {...state};

    switch (action.action) {
        case 'HYDRATE': {
            const incoming = (action.payload as FilterState) ?? createEmptyFilterState();
            nextState.current = {...incoming};
            nextState.draft = {...incoming};
            break;
        }
        case 'APPLY':
            nextState.current = {...state.draft};
            break;
        case 'CANCEL':
            nextState.draft = {...state.current};
            break;
        case 'RESET':
            nextState.draft = createEmptyFilterState();
            break;
        default:
            nextState.draft = applyChange(action.action as FilterActionType, (action as FilterChangeAction).payload, nextState.draft);
            break;
    }

    return nextState;
}

const TimeReportMobileFilterSheet = ({
    open,
    onClose,
    onApply,
    onClear,
    search,
    onSearchChange,
    filters,
    employees,
    projects,
    projectTypes,
    disabled,
    detailedByDay,
    onDetailedByDayChange,
    visibleDays,
    onVisibleDaysChange,
    disableEmployeesFilter,
}: Props) => {
    const intl = useIntl();
    const [filterState, dispatch] = useReducer(reducer, initialReducerState);
    const [draftDetailedByDay, setDraftDetailedByDay] = React.useState(false);
    const [draftVisibleDays, setDraftVisibleDays] = React.useState<VisibleDays>(VisibleDays.All);
    const visibleDayOptions = React.useMemo(() => ([
        { id: VisibleDays.All, label: intl.formatMessage({ id: Lx.Report.ALL_DAYS }) },
        { id: VisibleDays.Incorrect, label: intl.formatMessage({ id: Lx.Report.ONLY_INCORRECT_DAYS }) },
    ]), [intl]);

    useEffect(() => {
        if (open) {
            dispatch({ action: 'HYDRATE', payload: filters });
            setDraftDetailedByDay(detailedByDay);
            setDraftVisibleDays(visibleDays);
        } else {
            dispatch({ action: 'CANCEL' });
            setDraftDetailedByDay(detailedByDay);
            setDraftVisibleDays(visibleDays);
        }
    }, [detailedByDay, filters, open, visibleDays]);

    const handleApply = () => {
        dispatch({ action: 'APPLY' });
        onDetailedByDayChange(draftDetailedByDay);
        onVisibleDaysChange(draftVisibleDays);
        const result = onApply(filterState.draft);
        if (result && typeof (result as Promise<void>).then === 'function') {
            (result as Promise<void>).finally(onClose);
        } else {
            onClose();
        }
    };

    const handleClearFilters = () => {
        dispatch({ action: 'RESET' });
        setDraftDetailedByDay(false);
        setDraftVisibleDays(VisibleDays.All);
        onDetailedByDayChange(false);
        onVisibleDaysChange(VisibleDays.All);
        onClear();
    };

    if (!open || disabled) {
        return null;
    }

    const employeeItems = employees.map((employee) => ({
        id: employee.id,
        name: employee.name,
        originalId: employee.id,
    }));

    const projectItems = projects.map((project) => ({
        id: project.id,
        name: project.name,
        originalId: project.id,
    }));

    const projectTypeItems = projectTypes.map((projectType) => ({
        id: projectType.id,
        name: projectType.name,
        originalId: projectType.id,
    }));

    const showAbsencesLabel = intl.formatMessage({ id: Lx.Report.SHOW_ABSENCES, defaultMessage: 'Show absences' });

    return (
        <div className={styles.sheet}>
            <div className={styles.content}>
                <SearchInput
                    searchValue={search ?? ''}
                    onChange={onSearchChange}
                    width='100%'
                    disabled={disabled}
                />

                {!disableEmployeesFilter && (
                    <div className={styles.selectorBlock}>
                        <Selector
                            id={'time-report-mobile-filter-employees'}
                            label={Lx.General.EMPLOYEES}
                            items={employeeItems}
                            name={Lx.General.EMPLOYEES}
                            selected={filterState.draft.selectedEmployees}
                            setSelectedValues={(values) => dispatch({ action: 'EMPLOYEES_UPDATE', payload: values })}
                            intl={intl}
                            maxWidth={1000}
                            disabled={disabled}
                        />
                    </div>
                )}

                <div className={styles.selectorBlock}>
                    <Selector
                        id={'time-report-mobile-filter-projects'}
                        label={Lx.General.PROJECTS}
                        items={projectItems}
                        name={Lx.General.PROJECTS}
                        selected={filterState.draft.selectedProjects}
                        setSelectedValues={(values) => dispatch({ action: 'PROJECTS_UPDATE', payload: values })}
                        intl={intl}
                        maxWidth={1000}
                        disabled={disabled}
                    />
                </div>

                <div className={styles.selectorBlock}>
                    <Selector
                        id={'time-report-mobile-filter-project-types'}
                        label={Lx.General.PROJECT_TYPES}
                        items={projectTypeItems}
                        name={Lx.General.PROJECT_TYPES}
                        selected={filterState.draft.selectedProjectTypes}
                        setSelectedValues={(values) => dispatch({ action: 'PROJECTTYPE_UPDATE', payload: values })}
                        intl={intl}
                        maxWidth={1000}
                        disabled={disabled}
                    />
                </div>

                <div className={styles.toggleRow}>
                    <TSwitch
                        checked={filterState.draft.showAbsences ?? true}
                        onChange={(checked) => dispatch({ action: 'SHOW_ABSENCES_UPDATE', payload: checked })}
                        label={showAbsencesLabel}
                        name={'time-report-mobile-filter-show-absences'}
                        labelProps={{ className: styles.toggleSwitch }}
                        disabled={disabled}
                    />
                </div>
                <div className={styles.toggleRow}>
                    <TSwitch
                        checked={draftDetailedByDay}
                        onChange={setDraftDetailedByDay}
                        label='Detailed by day'
                        name={'time-report-mobile-filter-detailed-by-day'}
                        labelProps={{ className: styles.toggleSwitch }}
                        disabled={disabled}
                    />
                </div>
                {draftDetailedByDay && (
                    <div className={styles.dropdownBlock}>
                        <div className={styles.dropdownLabel}>Show days</div>
                        <div className={styles.dropdownField}>
                            <DropdownWrapper
                                className={`${formDropdownStyles.dropdown} ${styles.visibleDaysDropdown}`}
                                name={'time-report-mobile-filter-visible-days'}
                                value={draftVisibleDays}
                                options={visibleDayOptions}
                                showCheckIcon
                                selectedTextColor='secondary'
                                onChange={(_, value) => setDraftVisibleDays((value ?? VisibleDays.All) as VisibleDays)}
                            />
                        </div>
                    </div>
                )}
            </div>
            <Box className={styles.footer}>
                <ActionCancelButtonsArea
                    idActionLocalization={Lx.General.APPLY}
                    idCancelLocalization={Lx.General.RESET_FILTER}
                    onCancel={handleClearFilters}
                    onAction={handleApply}
                    disabled={disabled}
                />
            </Box>
        </div>
    );
};

export default TimeReportMobileFilterSheet;
